import React from 'react';
import { CheckOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import usePerx3Redesign from 'hooks/usePerx3Redesign';

import { describeOrbitContext, describeOrbitScreen } from '../contextLabel';
import OrbitGlyphText from '../glyphIcons';
import { TOrbit } from '../i18n/TModules';
import { orbitTokens, orbitType } from '../styles';
import { OrbitEntryContext } from '../types';

export interface IOrbitHandoffCardProps {
  /** Context the investigation was just carried into. */
  context: OrbitEntryContext;
}

/**
 * figma-spec-v3.md section 2 resolves frame 7's own contradiction: the card was `#B18CF5` on
 * `#B18CF5` (1.00:1, invisible) and is now `#8FD4B9` on `#17362F` (7.65:1), which section 2 names
 * as a change to adopt. With a real ground behind it the 12% wash the invisible fill forced is no
 * longer needed, so the fill ships opaque as drawn (`5032:11663`).
 */
const CARD_FILL = orbitTokens.handoffFill;
const CARD_INK = orbitTokens.handoffInk;

/**
 * The same chrome in the tone a failure needs. v3 draws no failed variant of this card, so the pair
 * is derived from the panel's own danger ink over the danger surface the error states already use:
 * `#EE6475` on `#0F1B2D` reads 5.24:1. PLA-9563 §4b / §5.
 */
const FAIL_FILL = orbitTokens.surfaceDanger;
const FAIL_INK = orbitTokens.danger;

export type OrbitOutcomeCardTone = 'positive' | 'negative';

const Card = styled.section<{ $tone: OrbitOutcomeCardTone }>`
  padding: 11px 13px;
  background: ${({ $tone }) => ($tone === 'negative' ? FAIL_FILL : CARD_FILL)};
  border: 1px solid ${({ $tone }) => ($tone === 'negative' ? FAIL_INK : CARD_INK)};
  border-radius: 11px;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-top: 5px;
  }
`;

const Top = styled.h3<{ $tone: OrbitOutcomeCardTone }>`
  margin: 0;
  padding-bottom: 2px;
  color: ${({ $tone }) => ($tone === 'negative' ? FAIL_INK : CARD_INK)};
  ${orbitType.containerTitle}
  font-weight: 700;
`;

const Body = styled.p`
  margin: 0;
  color: ${orbitTokens.threadMuted};
  ${orbitType.containerBody}
  font-weight: 400;
`;

const Steps = styled.ul`
  margin: 0;
  padding: 2px 0 0;
  list-style: none;

  > * + * {
    margin-top: 3px;
  }
`;

const Step = styled.li`
  display: flex;
  align-items: flex-start;
  color: ${orbitTokens.threadMuted};
  ${orbitType.containerBody}
  font-weight: 400;

  > * + * {
    margin-left: 6px;
  }
`;

const Tick = styled.span<{ $tone: OrbitOutcomeCardTone }>`
  flex: 0 0 9px;
  color: ${({ $tone }) => ($tone === 'negative' ? FAIL_INK : orbitTokens.successTick)};
  ${orbitType.containerBody}
  font-weight: 700;
`;

const Actions = styled.div`
  padding-top: 6px;
`;

export interface IOrbitOutcomeCardProps {
  tone?: OrbitOutcomeCardTone;
  /** The card's headline; the drawn strings carry their own leading glyph. */
  top: string;
  body?: string;
  /** One line per satisfied condition, each drawn with the card's tick (`5032:11669`). */
  steps?: string[];
  /** The card's single button (`5032:11697`), when the outcome offers a next step. */
  action?: React.ReactNode;
  testId?: string;
  topTestId?: string;
  stepTestId?: string;
}

/**
 * div.handoffCard `4952:7148` / `5032:11663` — 469 x 117 r11, `#17362F` under a `#8FD4B9` stroke,
 * a bold top line, a body line and tick steps. Three outcomes draw it: the investigation carried
 * into a new context, the Growth Planner handoff result (PLA-9563 §4b) and the generated report
 * (Desktop-14, PLA-9563 §5), so the chrome is one component and each caller supplies its copy.
 */
export const OrbitOutcomeCard: React.FC<IOrbitOutcomeCardProps> = ({
  tone = 'positive',
  top,
  body,
  steps = [],
  action,
  testId = 'orbit-handoff-card',
  topTestId = 'orbit-handoff-top',
  stepTestId = 'orbit-handoff-step'
}) => {
  const perx3 = usePerx3Redesign();
  // PERX3-REDESIGN round 7: the tick is the antd glyph under the flag and the drawn character
  // without it, and the headline's own leading glyph is drawn the same way. Both readings live in
  // the shared chrome so no caller of it regresses.
  const tick = perx3 ? <CheckOutlined /> : '✓';

  return (
    <Card $tone={tone} data-testid={testId} data-tone={tone}>
      <Top $tone={tone} data-testid={topTestId}>
        <OrbitGlyphText text={top} />
      </Top>
      {body !== undefined && body !== '' ? <Body>{body}</Body> : null}
      {steps.length > 0 ? (
        <Steps>
          {steps.map(step => (
            <Step key={step} data-testid={stepTestId}>
              <Tick $tone={tone} aria-hidden="true">
                {tone === 'negative' ? '!' : tick}
              </Tick>
              <span>{step}</span>
            </Step>
          ))}
        </Steps>
      ) : null}
      {action !== undefined && action !== null ? (
        <Actions data-testid={`${testId}-action`}>{action}</Actions>
      ) : null}
    </Card>
  );
};

/**
 * Recorded in the transcript when the user answers the context-conflict card with Continue with New
 * Investigation: the screen moved, the investigation did not.
 */
const OrbitHandoffCard: React.FC<IOrbitHandoffCardProps> = ({ context }) => {
  const { t } = useMultiLanguageTranslation();
  const screen = describeOrbitScreen(t, context);

  return (
    <OrbitOutcomeCard
      top={t(`${TOrbit}.handoff.opened`, { context: describeOrbitContext(t, context) })}
      body={t(`${TOrbit}.handoff.body`)}
      steps={[
        screen === undefined
          ? t(`${TOrbit}.handoff.authorizationGeneric`)
          : t(`${TOrbit}.handoff.authorization`, { screen }),
        t(`${TOrbit}.handoff.carried`)
      ]}
    />
  );
};

export default OrbitHandoffCard;
